import React from "react";
import {
    ACCESS_PROTECTED,
    ACCESS_PUBLIC,
    ACCESS_UNKNOWN,
    normalizeRoutePath,
    resolveRouteAccess,
} from "./permissionMap";

const IS_DEV = process.env.NODE_ENV !== "production";

/**
 * Reads the destination of a menu item.
 *
 * Every field a menu item has ever used to carry its destination is checked,
 * so a refactor that renames the field cannot silently disable permission
 * filtering — which is exactly what happened when `label: <Link to="..." />`
 * was replaced by `label: "..."` plus a new `to` field.
 */
export function getMenuItemPath(item) {
    if (!item) return null;

    for (const key of ["to", "link", "href", "path", "url"]) {
        const value = item[key];
        if (typeof value === "string" && value) {
            return normalizeRoutePath(value);
        }
    }

    // Legacy shape: the destination lived on a <Link> inside the label.
    const fromElement = readPathFromElement(item.label) || readPathFromElement(item.title);
    if (fromElement) return normalizeRoutePath(fromElement);

    return null;
}

function readPathFromElement(node, depth = 0) {
    if (depth > 3 || !React.isValidElement(node)) return null;

    const props = node.props || {};
    if (typeof props.to === "string" && props.to) return props.to;
    if (typeof props.href === "string" && props.href) return props.href;

    const children = React.Children.toArray(props.children);
    for (const child of children) {
        const found = readPathFromElement(child, depth + 1);
        if (found) return found;
    }

    return null;
}

const warnedPaths = new Set();
function warnUnmapped(item, path) {
    if (!IS_DEV) return;
    const identity = path || `key:${item?.key ?? "unknown"}`;
    if (warnedPaths.has(identity)) return;
    warnedPaths.add(identity);

    console.warn(
        `[permissions] Menu item ${identity} has no permission mapping and is not in PUBLIC_ROUTES, so it is hidden. ` +
        `Add it to ROUTE_PERMISSIONS, add it to PUBLIC_ROUTES, or set \`permission\` / \`public: true\` on the item.`
    );
}

/**
 * Decides whether a single leaf item is visible.
 *
 * Fail-closed: an item whose destination cannot be resolved is hidden. The old
 * behaviour ("no requirement found means everyone may see it") turned every
 * typo and every refactor into a silent permission leak.
 */
function isLeafVisible(item, canAccess, strict) {
    // Non-interactive entries are structural, not navigational.
    if (item.type === "divider") return true;

    // 1. An explicit declaration on the item always wins.
    if (item.public === true) return true;
    if (item.permission != null) return canAccess(item.permission);

    // 2. Otherwise resolve the destination against the route map.
    const path = getMenuItemPath(item);
    const { status, requirement } = resolveRouteAccess(path);

    if (status === ACCESS_PUBLIC) return true;
    if (status === ACCESS_PROTECTED) return canAccess(requirement);

    // 3. status === ACCESS_UNKNOWN
    warnUnmapped(item, path);
    return !strict;
}

/**
 * Removes every menu entry the current user may not reach.
 *
 * Ant Design's Menu does not reliably honour a generic `hidden` field, so
 * unauthorised entries are removed from the array instead. Parent groups
 * disappear automatically once none of their children survive.
 *
 * @param {Array}    items     menu tree
 * @param {Function} canAccess from usePermissions()
 * @param {Object}   options   { strict = true } — set strict: false to fall
 *                             back to the old fail-open behaviour.
 */
export function filterMenuItemsByPermissions(items, canAccess, options = {}) {
    const { strict = true } = options;

    if (!Array.isArray(items)) return [];
    if (typeof canAccess !== "function") return [];

    return items.reduce((visibleItems, item) => {
        if (!item) return visibleItems;

        const hasChildren = Array.isArray(item.children);

        if (hasChildren) {
            // A group may carry its own requirement on top of its children's.
            if (item.permission != null && !canAccess(item.permission)) {
                return visibleItems;
            }

            const visibleChildren = filterMenuItemsByPermissions(
                item.children,
                canAccess,
                options
            );

            if (visibleChildren.length > 0) {
                visibleItems.push({ ...item, children: visibleChildren });
            }

            return visibleItems;
        }

        if (isLeafVisible(item, canAccess, strict)) {
            visibleItems.push(item);
        }

        return visibleItems;
    }, []);
}

/**
 * Development helper: lists every menu destination with no permission mapping.
 * Run it once against your full menu tree to catch gaps before shipping.
 */
export function auditMenuPermissions(items, seen = []) {
    if (!Array.isArray(items)) return seen;

    items.forEach((item) => {
        if (!item) return;

        if (Array.isArray(item.children)) {
            auditMenuPermissions(item.children, seen);
            return;
        }

        if (item.public === true || item.permission != null) return;
        if (item.type === "divider") return;

        const path = getMenuItemPath(item);
        if (resolveRouteAccess(path).status === ACCESS_UNKNOWN) {
            seen.push({ key: item.key, path });
        }
    });

    if (IS_DEV && seen.length) {
        console.warn("[permissions] Unmapped menu items:", seen);
    }

    return seen;
}
